import type { RecordScope } from "@tlcrm/shared";

/**
 * How the CRM writes numbers and moments for people. Pure, tested in format.test.ts. Dates of
 * birth and other calendar dates go through shared's toUsDate; this file is for amounts and for
 * moments in time.
 */

/** "$1,000,000", or "$58.24" with cents. Null renders as an en dash, the ledger's "nothing". */
export function money(n: number | null | undefined, cents = false): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "–";
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
}

/** "$250k", "$1.5M": face amounts in a dense column. */
export function moneyShort(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "–";
  if (n >= 1_000_000) return `$${trim(n / 1_000_000)}M`;
  if (n >= 10_000) return `$${trim(n / 1000)}k`;
  return money(n);
}

function trim(n: number): string {
  return n.toFixed(n % 1 === 0 ? 0 : 1);
}

/** Lead age, counting: "42s", "4m 12s", "1h 5m", "3d". The desk's own layout. */
export function age(ms: number): string {
  const t = Math.max(0, Math.floor((Number.isFinite(ms) ? ms : 0) / 1000));
  if (t < 60) return `${t}s`;
  if (t < 3600) return `${Math.floor(t / 60)}m ${t % 60}s`;
  if (t < 86400) return `${Math.floor(t / 3600)}h ${Math.floor((t % 3600) / 60)}m`;
  return `${Math.floor(t / 86400)}d`;
}

/** Call length: "0:42", "12:05", "1:02:10". */
export function duration(seconds: number | null | undefined): string {
  const t = Math.max(0, Math.floor(seconds ?? 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = String(t % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * A moment, as close as it needs to be: "3:05 PM" today, "Yesterday 3:05 PM", "Mon 3:05 PM"
 * within six days either side, and the calendar date (MM-DD-YYYY, as every date is typed and
 * read here) beyond that.
 */
export function when(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "–";
  const d = new Date(iso);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (sameDay(d, now)) return time;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return `Yesterday ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (sameDay(d, tomorrow)) return `Tomorrow ${time}`;
  const days = (d.getTime() - now.getTime()) / 86_400_000;
  if (Math.abs(days) < 6) return `${d.toLocaleDateString("en-US", { weekday: "short" })} ${time}`;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}-${dd}-${d.getFullYear()}`;
}

/** How long ago, in a word or two: "just now", "12m ago", "3h ago", then the moment itself. */
export function ago(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "–";
  const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 6 * 3600) return `${Math.floor(s / 3600)}h ago`;
  return when(iso, new Date(now));
}

/** Talk time: "3h 12m", "42m". */
export function hoursMinutes(seconds: number): string {
  const m = Math.round(Math.max(0, seconds) / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
}

/** "Denise Okafor" → "DO". */
export function initials(name: string | null | undefined): string {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();
}

/** Height in inches as people say it: 70 → 5′10″. */
export function height(inches: number | null | undefined): string {
  if (!inches) return "–";
  return `${Math.floor(inches / 12)}′${inches % 12}″`;
}

/** Whose records a list or report covers, in words. */
export const SCOPE_LABEL: Record<RecordScope, string> = {
  me: "Mine",
  team: "My team",
  branch: "My branch",
  all: "Everyone",
};
